import { DashboardDataQuery } from 'components/Dashboard/hooks/DashboardData.generated';
import Table from 'components/Table/Table';
import { curr } from 'helpers/curr';
import React from 'react';
import { useClientSidePagination } from 'toolbox/src/hooks/usePagination';
import { useClientSideSortedTable } from 'toolbox/src/hooks/useSortedTable';
import { CurrencyFlag } from 'utils/Flag';
import JcapPagination from '../JcapPagination/JcapPagination';

interface DashboardBankAccountsTableProps {
  bank_accounts: DashboardDataQuery['organisations_by_pk']['bank_accounts'];
  bankName: string;
  bankImageUrl?: string;
}

export const DashboardBankAccountsTable: React.FC<DashboardBankAccountsTableProps> = ({
  bank_accounts,
  bankName,
  bankImageUrl,
}) => {
  const filteredBankAccounts = bank_accounts.filter(
    (bank_account) => bank_account.bank.name === bankName,
  );
  const [
    paginatedData,
    paginationProps,
  ] = useClientSidePagination(filteredBankAccounts, { resultsPerPage: 5 });

  const { onColumnClick, sortingState, sortedData } = useClientSideSortedTable(
    paginatedData,
    {
      defaultOrderBy: 'Balance',
      sort: (a, b, sortingState) => {
        switch (sortingState.columnId) {
          case 'Business Unit': {
            if (sortingState.order === 'asc') {
              return b.currency_book.business_unit.name.localeCompare(
                a.currency_book.business_unit.name,
              );
            } else {
              return a.currency_book.business_unit.name.localeCompare(
                b.currency_book.business_unit.name,
              );
            }
          }
          case 'Currency Book': {
            if (sortingState.order === 'asc') {
              return b.currency_book.name.localeCompare(a.currency_book.name);
            } else {
              return a.currency_book.name.localeCompare(b.currency_book.name);
            }
          }
          case 'Account': {
            if (sortingState.order === 'asc') {
              return b.account_id.localeCompare(a.account_id);
            } else {
              return a.account_id.localeCompare(b.account_id);
            }
          }
          case 'Type': {
            if (sortingState.order === 'asc') {
              return b.product_type.localeCompare(a.product_type);
            } else {
              return a.product_type.localeCompare(b.product_type);
            }
          }
          case 'Balance': {
            if (sortingState.order === 'asc') {
              return (
                a.actual_balances?.[0]?.balance_gbp -
                b.actual_balances?.[0]?.balance_gbp
              );
            } else {
              return (
                b.actual_balances?.[0]?.balance_gbp -
                a.actual_balances?.[0]?.balance_gbp
              );
            }
          }

          case 'Interest Rate': {
            if (!a.actual_balances?.[0]?.interest_rate) {
              return 1;
            } else if (!b.actual_balances?.[0]?.interest_rate) {
              return -1;
            }
            if (sortingState.order === 'asc') {
              return (
                a.actual_balances?.[0]?.interest_rate -
                b.actual_balances?.[0]?.interest_rate
              );
            } else {
              return (
                b.actual_balances?.[0]?.interest_rate -
                a.actual_balances?.[0]?.interest_rate
              );
            }
          }
          default:
            return 0;
        }
      },
    },
  );

  return (
    <div>
      <div className="flex justify-between mb-4 space-x-4 text-lg font-semibold text-gray-700">
        {bankImageUrl && (
          <img
            src={bankImageUrl}
            alt={bankName}
            className="object-contain h-8"
          />
        )}
      </div>
      <Table
        onColumnClick={onColumnClick}
        sortingState={sortingState}
        gridTemplateColumns="180px 190px 1.5fr 100px 1fr 110px"
        data={sortedData}
        renderExpandableRow={() => <></>}
        columns={[
          {
            Header: 'Account',
            accessor: (account) => account.account_id,
          },
          {
            Header: 'Business Unit',
            accessor: (account) => {
              return account.currency_book?.business_unit?.name;
            },
          },
          {
            Header: 'Currency Book',
            accessor: (account) => (
              <div className="flex items-center">
                <div className="flex-shrink-0 ">
                  <CurrencyFlag
                    currencyCode={account.currency?.prefix}
                    className="inline w-8 h-auto mr-3"
                  />
                </div>
                <div className="flex-shrink overflow-x-hidden">
                  <p className="font-semibold truncate">
                    {account.currency_book?.name}
                  </p>
                </div>
              </div>
            ),
          },
          {
            Header: 'Type',
            accessor: (bank_account) => {
              return (
                <div>
                  {bank_account.product_type === 'NOTICE' ? (
                    <div>
                      <p>{bank_account.notice_days}D NOTICE</p>
                    </div>
                  ) : bank_account.product_type === 'FIXED' ? (
                    <div>
                      <p>FIXED</p>

                      <p className="text-xs text-gray-600">
                        Matures {bank_account.fixed_maturity_date}
                      </p>
                    </div>
                  ) : (
                    <div>{bank_account.product_type}</div>
                  )}
                </div>
              );
            },
          },
          {
            Header: 'Balance',
            accessor: (account) => {
              return curr(
                account.actual_balances?.[0]?.balance,
                account.currency?.prefix,
              );
            },
          },
          {
            Header: 'Interest Rate',
            accessor: (account) => {
              const interest = account?.actual_balances?.[0]?.interest_rate;

              return (
                <div className="text-xs">
                  {interest === 0
                    ? '-'
                    : Number(interest)
                        .toFixed(4)
                        .replace(/(\.[1-9]+)([0]+)$/, '$1')
                        .replace('0.0000', '') + '%'}
                </div>
              );
            },
          },
        ]}
      />
      <JcapPagination {...paginationProps} />
    </div>
  );
};

export default DashboardBankAccountsTable;
